import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import useResize from 'hooks/useResize';
import Grid from 'components/Grid';
import fieldRenderProps from 'components/FinalForm/fieldRenderProps';
import { GuavaPropTypes } from 'propTypes';
import { isMobile } from 'helpers';
import FormField from './FormField';
import CustomFieldBase from '../../Fields/Partials/CustomFieldBase';
import RatingLabelRow from './Partials/RatingLabelRow';

const CompactFormControlLabel = styled(FormControlLabel)`
  margin: 0;

  max-width: 55px;
  width: 100%;
  min-width: 42px;
  justify-self: center;

  .MuiRadio-root {
    margin-top: -6px;
    margin-bottom: -6px;
  }

  .Mui-disabled.Mui-checked {
    color: ${({ theme }) => theme.palette.primary.main}
  }
`;

const TextLabel = styled('div')`
  line-height: 1em;
  min-width: min(80px, fit-content);
  display: inline-block;
  padding: 0 0.25em;
`;

const StyledLabel = styled('span')`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.palette.text.primary};
  font-weight: 500;
  text-align: center;
  display: block;

  .Mui-checked + span & {
    font-weight: 600;
  }
`;

const clampedLabel = css`
  ${TextLabel} {
      line-height: 1em;
      max-height: 2em;
      text-overflow: ellipsis;
      overflow: hidden;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      display: -webkit-box;
      justify-content: center;

      height: 2em;
      display: flex;
  }
`;

const AlignedLabel = styled(StyledLabel)`
  padding-top: 4px; // Hardcoded
`;

const LeftLabel = styled(AlignedLabel)`
  text-align: right;
`;

const RightLabel = styled(AlignedLabel)`
  text-align: left;
`;

const layouts = ({
  1: css``,
  2: css`
    & > label:nth-of-type(2n) ${TextLabel} { margin-top: 1.5em; }`,
  3: css`
    & > label:nth-of-type(2n-1) ${TextLabel} { margin-top: 1.5em; }`,
  4: css`
    ${clampedLabel}
    & > label:nth-of-type(3n-1) ${TextLabel} { margin-top: 2.5em; }
    & > label:nth-of-type(3n) ${TextLabel} { margin-top: 5em; }
  `,
  5: css`
    ${clampedLabel}
    & > label:nth-of-type(3n-1) ${TextLabel} { margin-top: 5em; }
    & > label:nth-of-type(3n) ${TextLabel} { margin-top: 2.5em; }
  `,
  6: css`
    ${clampedLabel}
    & > label:nth-of-type(3n-1) ${TextLabel} { margin-top: 2.5em; } // 0.5em + 1em + 1em
    & > label:nth-of-type(3n) ${TextLabel} { margin-top: 5em; } // 0.5em + 1em + 1em + 0.5 em
  `,
  7: css`
    ${clampedLabel}
    & > label:nth-of-type(3n-1) ${TextLabel} { margin-top: 5em; }
    & > label:nth-of-type(3n) ${TextLabel} { margin-top: 2.5em; }
  `,
});
const maxLayout = Math.max(...Object.keys(layouts));
const getLayoutStyle = (layout) => layouts[layout] || getLayoutStyle(maxLayout);

const styledRadioGroupConfig = {
  shouldForwardProp: prop => prop !== 'fullWidth',
};
const StyledRadioGroup = styled(RadioGroup, styledRadioGroupConfig)`
  display: flex;
  flex-wrap: nowrap;

  ${({ fullWidth }) => fullWidth && css`
    display: flex;
    justify-content: center;

    width: 100%;

    display: grid;
    grid-auto-columns: minmax(0, 150px);
    grid-auto-flow: column;
  `}

  ${({ layout }) => getLayoutStyle(layout)}
`;

const Label = ({ options, optionLabel, value, labelPlacement }) => {
  const label = options?.[value];

  let parts = [];

  if (['value', 'both'].includes(optionLabel) && value !== null && value !== undefined) {
    parts.push(<TextLabel key="value">{value}</TextLabel>);
  }

  if (['label', 'both', optionLabel].includes(optionLabel) && label) {
    parts.push(<TextLabel key="label">{label}</TextLabel>);
  }

  if (!parts.length) {
    return null;
  }

  if (labelPlacement === 'top') {
    parts = parts.reverse();
  }

  return (
    <StyledLabel>{parts}</StyledLabel>
  );
};

Label.propTypes = {
  options: PropTypes.object,
  optionLabel: PropTypes.oneOf(['none', 'value', 'both', 'label']),
  value: PropTypes.number,
  labelPlacement: PropTypes.oneOf(['top', 'bottom']),
};

const doNodesOverlap = (a, b) => {
  const aRect = a.getBoundingClientRect();
  const bRect = b.getBoundingClientRect();

  return !(
    aRect.top > bRect.bottom
    || aRect.right < bRect.left
    || aRect.bottom < bRect.top
    || aRect.left > bRect.right
  );
};

const getHasOverlap = (nodes) => nodes && !!Array.from(nodes).find((a, idx) => (
  Array.from(nodes).slice(idx + 1).find(b => (
    doNodesOverlap(a, b)
  ))
));

export const LikertFieldComponent = ({
  input: { name, value, onChange },
  label,
  start: $start = 1, max: $max = 7,
  required, readOnly, fullWidth = true,
  options, optionLabel = 'value',
  bottomLeftLabel, bottomRightLabel, bottomMidLabel,
  topLeftLabel, topRightLabel, topMidLabel,
  leftLabel, rightLabel }) => {

  const start = Number.isFinite(+$start) ? +$start : 0;
  const max = Number.isFinite(+$max) ? +$max : 7;

  const labelPlacement = 'bottom';

  const ref = useRef();

  const [layout, setLayout] = useState(1);

  const checkOverlap = useCallback(() => {
    const nodes = ref.current?.querySelectorAll(`${TextLabel}`);
    const hasOverlap = getHasOverlap(nodes);

    if (hasOverlap) {
      setLayout(c => c + 1);
    }
  }, []);

  useResize(() => {
    // On resize, start trying from the first layout again (1, 2, 3, ...)
    setLayout(1);
    setTimeout(() => checkOverlap(true));
  }, { passive: true });

  useLayoutEffect(() => {
    // When we reach the last layout, then stop... no more ideas
    if (layout < maxLayout) {
      checkOverlap();
    }
  }, [checkOverlap, ref, layout]);

  return (
    <CustomFieldBase label={label} margin="none" required={required}>
      <Grid container spacing={1} direction="column" wrap="nowrap">

        {(topLeftLabel || topMidLabel || topRightLabel) && (
          <Grid item>
            <RatingLabelRow left={topLeftLabel} mid={topMidLabel} right={topRightLabel} />
          </Grid>
        )}

        <Grid item xs>
          <StyledRadioGroup row ref={ref} name={name} fullWidth={fullWidth} layout={layout} onChange={(e, v) => onChange(v)} value={value}>
            {leftLabel && <LeftLabel>{leftLabel}</LeftLabel>}
            {Array.from(new Array(max)).map((v, idx) => (
              <CompactFormControlLabel
                key={idx}
                onClick={(e) => e.target.value === value ? onChange(null) : null}
                value={start + idx}
                label={<Label options={options} optionLabel={optionLabel} value={start + idx} labelPlacement={labelPlacement} />}
                labelPlacement={labelPlacement}
                disabled={readOnly}
                control={<Radio size={isMobile ? 'small' : undefined} />}
              />
            ))}
            {rightLabel && <RightLabel>{rightLabel}</RightLabel>}
          </StyledRadioGroup>
        </Grid>

        {(bottomLeftLabel || bottomMidLabel || bottomRightLabel) && (
          <Grid item>
            <RatingLabelRow left={bottomLeftLabel} mid={bottomMidLabel} right={bottomRightLabel} />
          </Grid>
        )}
      </Grid>
    </CustomFieldBase>
  );
};

LikertFieldComponent.propTypes = {
  ...fieldRenderProps,

  max: GuavaPropTypes.isFinite,
  start: GuavaPropTypes.isFinite,
  fullWidth: PropTypes.bool,
  options: PropTypes.object,
  optionLabel: PropTypes.oneOf(['none', 'value', 'label', 'both']),

  bottomLeftLabel: PropTypes.string,
  bottomRightLabel: PropTypes.string,
  bottomMidLabel: PropTypes.string,

  topLeftLabel: PropTypes.string,
  topRightLabel: PropTypes.string,
  topMidLabel: PropTypes.string,
};

const LikertField = (args) => (
  <FormField component={LikertFieldComponent} {...args} />
);


export default LikertField;
